/**
 * «Назначить вариантам плана» (ADR-066 D2).
 *
 * Карта НЕ принадлежит варианту плана: один и тот же «Интенсив» назначается
 * нескольким вариантам плана сезона, поэтому назначение — отдельное действие,
 * а не поле карты. Назначение живёт на уровне ХОЗЯЙСТВА (вариант плана =
 * хозяйство × сезон), отсюда выбор хозяйства первым шагом.
 */
import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useFarms } from '@/lib/reference/catalogScopes';
import {
  formatNumber,
  techCardKeys,
  usePlanVariants,
  type ScenarioAssignment,
  type TechCard,
} from '@/lib/planning/techCards';

export function AssignPlanVariantsDialog({
  card,
  seasonId,
  canAssign,
  busy,
  onAssign,
  onUnassign,
  onClose,
}: {
  card: TechCard;
  seasonId: string;
  canAssign: boolean;
  busy: boolean;
  onAssign: (scenarioId: string) => void;
  onUnassign: (scenarioId: string) => void;
  onClose: () => void;
}) {
  const farms = useFarms();
  // Карта хозяйства назначается только своему хозяйству — предвыбираем его,
  // чтобы не предлагать заведомо отказной вариант (бэк ответит 422).
  const [farmId, setFarmId] = useState<string | null>(card.farm_id);
  const plans = usePlanVariants(farmId, seasonId);

  const assignments = useQueries({
    queries: (plans.data ?? []).map((plan) => ({
      queryKey: techCardKeys.assignments(plan.id),
      queryFn: () =>
        api.get<ScenarioAssignment[]>(`/api/v1/planning/scenarios/${plan.id}/tech-cards`),
      staleTime: 30_000,
    })),
  });

  const assignedIn = new Set(
    (plans.data ?? [])
      .filter((_, i) => (assignments[i]?.data ?? []).some((a) => a.tech_card_id === card.id))
      .map((p) => p.id),
  );

  const farmOptions = [...(farms.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Назначить вариант «{card.name}» вариантам плана</DialogTitle>
          <DialogDescription>
            Один вариант техкарты можно назначить нескольким вариантам плана сезона — карта
            не принадлежит плану, она нормирует культуру.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {card.farm_id === null && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground">Хозяйство:</span>
              {farmOptions.map((farm) => (
                <button
                  key={farm.id}
                  type="button"
                  aria-pressed={farm.id === farmId}
                  onClick={() => setFarmId(farm.id)}
                  className={
                    farm.id === farmId
                      ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
                      : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                  }
                >
                  {farm.name}
                </button>
              ))}
            </div>
          )}

          {!farmId && (
            <p className="text-muted-foreground">
              Выберите хозяйство — варианты плана сезона ведутся по хозяйству.
            </p>
          )}

          {farmId && plans.isLoading && <Skeleton className="h-24 w-full" />}

          {farmId && !plans.isLoading && (plans.data ?? []).length === 0 && (
            <p className="text-muted-foreground">
              У этого хозяйства ещё нет ни одного варианта плана на сезон. Заведите его в
              планировании сезона — тогда карту будет чему назначать.
            </p>
          )}

          <ul className="space-y-1">
            {(plans.data ?? []).map((plan) => {
              const assigned = assignedIn.has(plan.id);
              return (
                <li key={plan.id} className="flex min-h-10 flex-wrap items-center gap-2 py-2 leading-6">
                  <span className="text-foreground">{plan.name}</span>
                  {plan.is_selected && <Badge variant="primary">план сезона</Badge>}
                  <span className="text-xs text-muted-foreground">
                    {formatNumber(plan.planned_area_ha)} га
                  </span>
                  {assigned && <Badge variant="outline">назначен</Badge>}
                  {canAssign && (
                    <Button
                      className="ml-auto"
                      size="sm"
                      variant={assigned ? 'ghost' : 'outline'}
                      disabled={busy}
                      onClick={() => (assigned ? onUnassign(plan.id) : onAssign(plan.id))}
                    >
                      {assigned ? 'Снять назначение' : 'Назначить'}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>

          {!canAssign && (
            <p className="text-muted-foreground">
              Смотреть можно, назначать — нет: у вас нет права правки техкарт.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Готово</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
